import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Summary } from '../../shared/types';
import { api } from '../lib/api';
import { useStore } from '../lib/store';
import { buildPeriod } from '../lib/period';
import { formatDuration } from '../lib/duration';
import { formatINR, MARK } from '../lib/format';
import { PageHeader } from '../components/AppShell';
import { KpiTile } from '../components/KpiTile';
import { Breakdown, HoursChart, MoneyChart } from '../components/charts';
import { EmptyState, ErrorBanner, Spinner } from '../components/ui';

function Panel({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="card px-5 py-4">
      <header className="mb-3.5">
        <h2 className="text-[15px]">{title}</h2>
        {subtitle && <p className="mt-0.5 text-[12px] text-[var(--color-ink-muted)]">{subtitle}</p>}
      </header>
      {children}
    </section>
  );
}

export function Dashboard() {
  const { weekStartsOn, settings, activeProjects } = useStore();
  const [week, setWeek] = useState<Summary | null>(null);
  const [month, setMonth] = useState<Summary | null>(null);
  const [year, setYear] = useState<Summary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const periods = useMemo(() => {
    const now = new Date();
    return {
      week: buildPeriod('week', now, weekStartsOn),
      month: buildPeriod('month', now, weekStartsOn),
      year: buildPeriod('year', now, weekStartsOn),
    };
  }, [weekStartsOn]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.summary(periods.week.from, periods.week.to, 'day'),
      api.summary(periods.month.from, periods.month.to, 'day'),
      api.summary(periods.year.from, periods.year.to, 'month'),
    ])
      .then(([w, m, y]) => {
        setWeek(w);
        setMonth(m);
        setYear(y);
        setError(null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not load the dashboard'))
      .finally(() => setLoading(false));
  }, [periods]);

  if (loading) return <Spinner label="Loading dashboard" />;

  if (error) {
    return (
      <>
        <PageHeader title="Dashboard" />
        <ErrorBanner message={error} />
      </>
    );
  }
  if (!week || !month || !year) return null;

  // Days actually worked, not calendar days — an average over idle days misleads.
  const daysWorked = month.buckets.filter((b) => b.minutes > 0).length;
  const averagePerDay = daysWorked ? Math.round(month.totals.minutes / daysWorked) : 0;

  const hasData = year.totals.entry_count > 0;

  if (!hasData) {
    return (
      <>
        <PageHeader title="Dashboard" subtitle={settings.owner_name ? `Welcome back, ${settings.owner_name}.` : undefined} />
        <div className="card">
          <EmptyState
            title="Your dashboard fills in as you log time"
            description={
              activeProjects.length === 0
                ? 'Start by creating a project — its hourly rate is what turns logged hours into rupees.'
                : 'Add a few timesheet rows and the totals, charts and compensation will appear here.'
            }
            action={
              <Link
                to={activeProjects.length === 0 ? '/projects' : '/timesheet'}
                className="btn btn-primary"
              >
                {activeProjects.length === 0 ? 'Create a project' : 'Log some time'}
              </Link>
            }
          />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle={settings.owner_name ? `Welcome back, ${settings.owner_name}.` : 'Your hours at a glance.'}
        actions={
          <Link to="/timesheet" className="btn btn-primary">
            Log time
          </Link>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiTile
          label="This week"
          value={formatDuration(week.totals.minutes)}
          hint={periods.week.label}
          accent="blue"
        />
        <KpiTile
          label="This month"
          value={formatDuration(month.totals.minutes)}
          hint={`${daysWorked} ${daysWorked === 1 ? 'day' : 'days'} worked`}
          accent="cyan"
        />
        <KpiTile
          label="Month compensation"
          value={formatINR(month.totals.amount_inr)}
          hint={periods.month.label}
          accent="coral"
        />
        <KpiTile
          label="Average per working day"
          value={formatDuration(averagePerDay)}
          hint={`${formatDuration(year.totals.minutes)} logged in ${periods.year.label}`}
          accent="gold"
        />
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Panel title="Hours this month" subtitle={`Daily total across ${periods.month.label}`}>
            <HoursChart data={month.buckets} height={230} />
          </Panel>
        </div>

        <Panel title="Where the time went" subtitle={`By type of work, ${periods.month.label}`}>
          {month.byType.length === 0 ? (
            <p className="py-8 text-center text-[13px] text-[var(--color-ink-muted)]">
              Nothing logged this month yet.
            </p>
          ) : (
            <Breakdown
              accent={MARK.type}
              items={month.byType.map((t) => ({
                label: t.work_type,
                minutes: t.minutes,
                meta: `${Math.round((t.minutes / month.totals.minutes) * 100)}%`,
              }))}
            />
          )}
        </Panel>
      </div>

      <div className="mt-5 grid gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Panel title="Compensation through the year" subtitle={`Monthly earnings across ${periods.year.label}`}>
            <MoneyChart data={year.buckets} height={220} />
          </Panel>
        </div>

        <Panel title="By project" subtitle={periods.year.label}>
          {year.byProject.length === 0 ? (
            <p className="py-8 text-center text-[13px] text-[var(--color-ink-muted)]">No projects logged yet.</p>
          ) : (
            <Breakdown
              accent={MARK.hours}
              items={year.byProject.map((p) => ({
                label: p.project_name,
                minutes: p.minutes,
                color: p.project_color,
                meta: formatINR(p.amount_inr),
              }))}
            />
          )}
        </Panel>
      </div>
    </>
  );
}
